/**
 * The backstory Agent Alex tells on the intro screen before the first mission ever
 * (docs/design/story.md), as short segments: each is one recorded line with its own ElevenLabs
 * audio tag for the tone, said with a pause after it. Kept apart from the mission's first word
 * (phrases.ts, firstPrompt), so a floor never depends on the story.
 *
 * Each segment names the scene the screen shows while it plays (art/backgrounds/intro): Agent Alex
 * in the HQ hall, and from "Look!" on, the first foe on the big screen beside him, with Alex's
 * expression following the story. A segment with `cutAt` switches the scene at that word, timed
 * by the recording's alignment (voice manifest `words`); without it, at the segment's start.
 *
 * Audio tags ("[mysterious]") shape the recording only: captions and the browser's fallback voice
 * drop them (stripTags).
 */
import type { AgeBand } from '@/game/coach/phrases';

/**
 * The intro's scenes: Alex welcoming; Alex beside the empty screen; the first foe on the screen
 * with Alex presenting it, whispering, worried or excited.
 */
export type StoryScene =
  | 'welcome'
  | 'screen'
  /** A close-up of Alex whispering (no screen in view, so it can come before the reveal). */
  | 'whisper-close'
  | 'cat'
  | 'cat-whisper'
  | 'cat-worried'
  | 'cat-excited';

export interface StorySegment {
  text: string;
  scene: StoryScene;
  /** The word (as spoken, without punctuation) at which the scene starts; the segment's start if unset. */
  cutAt?: string;
  /** With `cutAt`: the scene from the segment's start until the cut. */
  lead?: StoryScene;
}

/** The foe is on the screen in this scene (the cut to it is the diagonal wipe). */
export const showsFoe = (scene: StoryScene) => scene.startsWith('cat');

export const STORY: Readonly<Record<AgeBand, readonly StorySegment[]>> = {
  // 5-7: the foes are pranksters; "foe" is learned from the story around it, never defined.
  // Reviewed as heard speech by fresh agents (docs/design/story.md).
  younger: [
    {
      text: '[warm] Hello there! I’m Agent Alex. I’m a spy, and I solve mysteries.',
      scene: 'welcome',
    },
    {
      text: '[cheerful] Welcome to our secret hideout, high up in this tall building. And guess what? Today, you’re a spy too!',
      scene: 'welcome',
    },
    {
      text: '[mysterious] But something strange is going on. Our snacks go missing, and our things get hidden. Some sneaky foes, troublemakers in disguise, keep playing tricks on us!',
      scene: 'screen',
    },
    {
      text: '[whispers] The foes wear masks, so we can’t tell who they are. But we’re going to catch them!',
      scene: 'whisper-close',
    },
    {
      text: '[curious] Each foe has a secret name. If we find out the secret name, their mask falls right off!',
      scene: 'screen',
    },
    {
      text: '[whispers] And I know where the first foe is hiding. Look!',
      lead: 'whisper-close',
      scene: 'cat-whisper',
      cutAt: 'Look',
    },
    {
      text: '[mysterious] It’s a sneaky cat in a purple mask. That cat hides our snacks, and it mixes up the letters on our signs, so nobody can read them!',
      scene: 'cat-worried',
    },
    {
      text: '[warm] You can find the cat’s secret name with your voice. Here’s how.',
      scene: 'cat',
    },
    {
      text: '[warm] I’ll show you a picture and say a word. Then you say the word back.',
      scene: 'cat',
    },
    {
      text: '[encouraging] Make the rrr in each word strong, like a tiger! Every time your rrr is strong, a new letter of the secret name pops up on the screen.',
      scene: 'cat-excited',
    },
    {
      text: '[excited] When all the letters are up, say the secret name out loud, and off comes the mask!',
      scene: 'cat-excited',
    },
    {
      text: '[warm] There’s a foe hiding on every floor of this building. The cat is here, on the first floor. Don’t worry, I’ll help you all the way.',
      scene: 'cat',
    },
  ],
  // 8-12: the foes are a spy gang.
  older: [
    {
      text: '[serious] Welcome to headquarters, new agent. You’re one of us now. My name is Agent Alex, and I’ll be your coach on this mission.',
      scene: 'welcome',
    },
    {
      text: '[mysterious] We have a problem. Some foes have sneaked into our headquarters building. They’re enemy spies, and each one works in disguise.',
      scene: 'screen',
    },
    {
      text: '[intrigued] Every foe hides behind a secret codename. Find out their codename, and we can unmask them.',
      scene: 'screen',
    },
    {
      text: '[serious] Our cameras just caught the first one. Look!',
      scene: 'cat',
      cutAt: 'Look',
    },
    {
      text: '[mysterious] A cat in a purple mask. It scrambles our secret messages, so nobody at headquarters can read them.',
      scene: 'cat-worried',
    },
    {
      text: '[confident] That’s where you come in. I’ll show you a word and say it. Then you say it back, and our voice scanner listens.',
      scene: 'cat',
    },
    {
      text: '[focused] Make the r sound in each word clear and strong. Every time your r sounds strong, the scanner uncovers a new letter of the codename.',
      scene: 'cat-excited',
    },
    {
      text: '[determined] When every letter is uncovered, say the codename out loud. If you need help reading it, tap the button with the speaker, and I’ll say it.',
      scene: 'cat',
    },
    {
      text: '[confident] When the scanner hears the codename, the mask comes off.',
      scene: 'cat-excited',
    },
    {
      text: '[warm] There’s a foe hiding on every floor of this building, and the cat is on this one. I’ll be right here with you.',
      scene: 'cat',
    },
  ],
};

/** Silence after each story segment (ms), so the story is told, not rattled off. */
export const STORY_PAUSE_MS = 650;

/** The line without its audio tags ("[mysterious] Hi" becomes "Hi"), for captions and fallback speech. */
export function stripTags(text: string): string {
  return text.replace(/\[[^\]]*\]\s*/g, '').trim();
}
